import React, { useEffect, useState } from 'react';
import {
  CityTaxMailbox,
  adminGetCityTaxMailboxes,
  adminCreateCityTaxMailbox,
  adminUpdateCityTaxMailbox,
  adminDeleteCityTaxMailbox,
} from '../api.cityTaxMailboxes';

const getToken = () => localStorage.getItem('token') || '';

const empty = (): CityTaxMailbox => ({ name: '', databoxId: '', address: '', district: '' });

const AdminCityTaxMailboxes: React.FC = () => {
  const [mailboxes, setMailboxes] = useState<CityTaxMailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<CityTaxMailbox>(empty());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setMailboxes(await adminGetCityTaxMailboxes(getToken()));
    } catch {
      setError('Failed to load mailboxes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (m: CityTaxMailbox) => { setForm({ ...m }); setEditingId(m.id!); setError(null); };
  const startNew  = () => { setForm(empty()); setEditingId('new'); setError(null); };
  const cancel    = () => { setEditingId(null); setError(null); };

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editingId === 'new') {
        await adminCreateCityTaxMailbox(form, getToken());
      } else {
        await adminUpdateCityTaxMailbox(editingId as number, form, getToken());
      }
      setEditingId(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await adminDeleteCityTaxMailbox(id, getToken());
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Delete failed');
    }
  };

  const field = (key: keyof CityTaxMailbox, label: string, placeholder = '') => (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <input
        className="form-control"
        value={(form[key] as string) || ''}
        placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <div style={{ maxWidth: 800, margin: '30px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Datové schránky — místní poplatek</h2>
        <button className="app-btn app-btn-blue app-btn-standard" onClick={startNew} disabled={editingId !== null}>
          + Přidat
        </button>
      </div>

      {error && <div style={{ color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* New / edit inline form */}
      {editingId !== null && (
        <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14, color: '#2d3748' }}>
            {editingId === 'new' ? 'Nová datová schránka' : 'Upravit'}
          </div>
          <div className="form-grid">
            {field('name', 'Název úřadu', 'např. Úřad MČ Praha 1')}
            {field('district', 'Část (pro řazení)', 'např. Praha 1')}
            {field('databoxId', 'ID datové schránky', 'např. abc1234')}
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Adresa</label>
              <input className="form-control" value={form.address || ''} placeholder="Celá adresa úřadu"
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="app-btn app-btn-green app-btn-standard" onClick={save} disabled={saving}>
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
            <button className="app-btn app-btn-standard" onClick={cancel} disabled={saving}>Zrušit</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: '#718096' }}>Načítám…</div>
      ) : mailboxes.length === 0 ? (
        <div style={{ color: '#718096', fontStyle: 'italic' }}>Žádné datové schránky. Přidejte první.</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {mailboxes.map(m => (
            <div key={m.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: 2 }}>
                  {m.name}
                  {m.district && <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#718096', fontWeight: 400 }}>({m.district})</span>}
                </div>
                {m.databoxId && <div style={{ fontSize: '0.85rem', color: '#4a5568' }}>ID DS: <strong>{m.databoxId}</strong></div>}
                {m.address && <div style={{ fontSize: '0.82rem', color: '#718096', marginTop: 2 }}>{m.address}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button className="app-btn app-btn-standard app-btn-compact" onClick={() => startEdit(m)} disabled={editingId !== null}>
                  Upravit
                </button>
                <button className="app-btn app-btn-red app-btn-compact" onClick={() => del(m.id!, m.name)} disabled={editingId !== null}>
                  Smazat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCityTaxMailboxes;
